/**
 * Format resolution + preview-URL building for the resume/document drawer.
 * Extracted from resume-drawer.tsx so .doc support is unit-testable.
 */

export type FileKind = 'pdf' | 'docx' | 'doc' | 'rtf' | 'unknown';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// Decide the format from the server-repaired mime_type first, then the filename
// extension as a tiebreaker. The resume URL/key extension is deliberately NOT
// used: ATS resumes are stored under .pdf keys regardless of their real format,
// so the bytes-derived mime_type (repaired by vhire-resume-ingest) is the only
// trustworthy signal.
export function resolveFileKind(mimeType?: string | null, fileName?: string | null): FileKind {
  const mime = (mimeType || '').toLowerCase();
  if (mime === 'application/pdf') return 'pdf';
  if (mime === DOCX_MIME) return 'docx';
  if (mime === 'application/msword') return 'doc';
  if (mime === 'application/rtf' || mime === 'text/rtf') return 'rtf';

  const ext = (fileName || '').toLowerCase().match(/\.(\w+)(?:[?#]|$)/)?.[1];
  if (ext === 'pdf') return 'pdf';
  if (ext === 'docx') return 'docx';
  if (ext === 'doc') return 'doc';
  if (ext === 'rtf') return 'rtf';
  return 'unknown';
}

/**
 * Preview iframe src for a file, or null when no inline preview exists.
 *
 * PDFs render the original directly. doc + docx use the server-rendered PDF at
 * /api/files/preview/{id} (vhire-resume-ingest renders .docx via mammoth and
 * legacy .doc via word-extractor — parse-time + backfill + 15-min self-heal
 * sweep). rtf/unknown have no renderer.
 */
export function buildPreviewSrc(
  fileUrl: string | null | undefined,
  fileId: string | null | undefined,
  kind: FileKind,
): string | null {
  if (!fileUrl) return null;
  if (kind === 'pdf') return `${fileUrl}#view=FitH`;
  if ((kind === 'docx' || kind === 'doc') && fileId && fileUrl.includes('/api/files/')) {
    const base = fileUrl.slice(0, fileUrl.indexOf('/api/files/'));
    return `${base}/api/files/preview/${fileId}#view=FitH`;
  }
  return null;
}
